/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

import apiClient from '@/services/api/client';
import type {
  FeatureAddon,
  FeatureAddonItem,
  FeatureImpactDto,
  CreateFeatureAddonRequest,
  UpdateFeatureAddonRequest,
} from '@/types/addon.types';

const ORG_BASE = '/billing/addons';
const ADMIN_BASE = '/admin/addons';

async function getActiveAddons(): Promise<FeatureAddonItem[]> {
  const res = await apiClient.get(ORG_BASE);
  return res.data.data;
}

async function getAvailableAddons(moduleCode: string): Promise<FeatureAddon[]> {
  const res = await apiClient.get(`${ORG_BASE}/${moduleCode}`);
  return res.data.data;
}

async function purchaseAddon(moduleCode: string, featureCode: string): Promise<FeatureAddonItem> {
  const res = await apiClient.post(`${ORG_BASE}/${moduleCode}/${featureCode}`);
  return res.data.data;
}

async function updateAddonUserCount(moduleCode: string, featureCode: string, userCount: number): Promise<FeatureAddonItem> {
  const res = await apiClient.put(`${ORG_BASE}/${moduleCode}/${featureCode}/users`, null, {
    params: { userCount },
  });
  return res.data.data;
}

async function cancelAddon(moduleCode: string, featureCode: string, reason?: string): Promise<void> {
  await apiClient.delete(`${ORG_BASE}/${moduleCode}/${featureCode}`, {
    params: reason ? { reason } : undefined,
  });
}

async function adminListAddons(): Promise<FeatureAddon[]> {
  const res = await apiClient.get(ADMIN_BASE);
  return res.data.data;
}

async function adminCreateAddon(data: CreateFeatureAddonRequest): Promise<FeatureAddon> {
  const res = await apiClient.post(ADMIN_BASE, data);
  return res.data.data;
}

async function adminUpdateAddon(id: string, data: UpdateFeatureAddonRequest): Promise<FeatureAddon> {
  const res = await apiClient.put(`${ADMIN_BASE}/${id}`, data);
  return res.data.data;
}

async function adminDeactivateAddon(id: string): Promise<void> {
  await apiClient.delete(`${ADMIN_BASE}/${id}`);
}

async function getAddonImpact(moduleCode: string, featureCode: string): Promise<FeatureImpactDto> {
  const res = await apiClient.get(`${ORG_BASE}/${moduleCode}/${featureCode}/impact`);
  return res.data.data;
}

export const addonService = {
  getActiveAddons,
  getAvailableAddons,
  purchaseAddon,
  updateAddonUserCount,
  cancelAddon,
  adminListAddons,
  adminCreateAddon,
  adminUpdateAddon,
  adminDeactivateAddon,
  getAddonImpact,
};
